import React from "react";
import LegalPage from "../components/LegalPage";

const sections = [
  {
    id: "promise",
    heading: "The promise, in one sentence",
    body: (
      <p className="text-[16px] sm:text-[18px] leading-[1.7] text-[#0f172a] font-semibold">
        Your renovation will be completed as scoped, on schedule, and to a workmanlike standard &mdash; or
        we&rsquo;ll make it right at no extra cost to you.
      </p>
    ),
  },
  {
    id: "coverage",
    heading: "What the Done-Right Guarantee covers",
    body: (
      <>
        <p>The Done-Right Guarantee covers, for every project booked through MeinHaus:</p>
        <ul className="list-disc pl-5 space-y-2 marker:text-[#F26B1F]">
          <li>
            <strong>Workmanship</strong> &mdash; we cover defective workmanship for{" "}
            <strong>12 months</strong> after your final sign-off on the project.
          </li>
          <li>
            <strong>Scope compliance</strong> &mdash; if a milestone or final deliverable doesn&rsquo;t match the
            itemized scope you approved in your dashboard, we step in.
          </li>
          <li>
            <strong>Schedule integrity</strong> &mdash; if a Pro fails to perform or abandons your project, we
            re-match you with another vetted Pro at no extra cost.
          </li>
          <li>
            <strong>Code compliance</strong> &mdash; we cover rework necessary to bring failed inspections into
            compliance with applicable building code.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "remedies",
    heading: "Your remedies",
    body: (
      <>
        <p>
          When a covered issue is raised, you choose one of the following remedies. We&rsquo;ll work with you to
          select the one that&rsquo;s right for your situation:
        </p>
        <ol className="list-decimal pl-5 space-y-2 marker:text-[#F26B1F] marker:font-bold">
          <li>
            <strong>Rework with the same Pro.</strong> The original Pro returns to complete or correct the work
            at no additional cost.
          </li>
          <li>
            <strong>Swap to another Pro.</strong> We re-match you with a different vetted Pro who finishes the
            work at the original quoted price.
          </li>
          <li>
            <strong>Refund of unreleased funds.</strong> Any milestone payments still held with MeinHaus are
            refunded to you in full.
          </li>
        </ol>
      </>
    ),
  },
  {
    id: "process",
    heading: "How to make a claim",
    body: (
      <>
        <p>To trigger the Guarantee:</p>
        <ol className="list-decimal pl-5 space-y-2 marker:text-[#1AA0DD] marker:font-bold">
          <li>
            Open a Guarantee claim from your dashboard within the coverage window (12 months from final sign-off)
            and describe the issue with photos.
          </li>
          <li>
            A MeinHaus project lead reviews your claim within <strong>3 business days</strong> and may schedule
            an inspection (in person or via video).
          </li>
          <li>
            We confirm coverage in writing, propose a remedy, and execute it within the timelines agreed in the
            claim resolution.
          </li>
        </ol>
        <p>
          If you and MeinHaus disagree on whether an issue is covered, you may request independent review by an
          accredited construction inspector. We split the inspector&rsquo;s reasonable fee 50/50 with you, and
          their finding is binding.
        </p>
      </>
    ),
  },
  {
    id: "exclusions",
    heading: "What is not covered",
    body: (
      <>
        <p>The Done-Right Guarantee does not cover:</p>
        <ul className="list-disc pl-5 space-y-2 marker:text-slate-400">
          <li>
            Normal wear and tear, settling cracks, or cosmetic issues that develop over time;
          </li>
          <li>
            Damage caused by you, third parties, pets, neglect, or improper maintenance;
          </li>
          <li>
            Acts of God (floods, fires, earthquakes, severe weather);
          </li>
          <li>
            Materials and fixtures you supplied directly to the Pro (manufacturer warranties apply to those);
          </li>
          <li>
            Modifications or repairs performed by anyone other than the original or replacement Pro;
          </li>
          <li>
            Work that was completed outside the agreed scope or undertaken off-platform without our prior
            written approval.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "vetting",
    heading: "Pro vetting standards",
    body: (
      <>
        <p>Every Pro on MeinHaus must, at the time of onboarding and at annual re-verification, hold:</p>
        <ul className="list-disc pl-5 space-y-2 marker:text-[#1AA0DD]">
          <li>active provincial business registration and trade licensing (where required);</li>
          <li>minimum <strong>CAD $2,000,000</strong> general liability insurance;</li>
          <li>active WSIB or provincial workers&rsquo; compensation coverage;</li>
          <li>at least 12 months of verified completed work in the relevant trade;</li>
          <li>satisfactory references and a clean dispute record.</li>
        </ul>
      </>
    ),
  },
  {
    id: "limits",
    heading: "Guarantee limits",
    body: (
      <p>
        The total amount payable by MeinHaus under the Guarantee for any single project is capped at the total
        contract value of that project (the &ldquo;Project Value&rdquo;). Indirect, incidental, or consequential
        damages are not covered.
      </p>
    ),
  },
  {
    id: "definitions",
    heading: "Definitions",
    body: (
      <ul className="list-disc pl-5 space-y-2 marker:text-slate-400">
        <li>
          <strong>Final sign-off</strong> — your written approval of the completed project in the MeinHaus
          dashboard.
        </li>
        <li>
          <strong>Coverage window</strong> — 12 consecutive months commencing on Final sign-off.
        </li>
        <li>
          <strong>Pro</strong> — the licensed contractor matched to your project through MeinHaus.
        </li>
      </ul>
    ),
  },
  {
    id: "contact",
    heading: "Questions",
    body: (
      <p>
        Email{" "}
        <a
          href="mailto:guarantee@meinhaus.ca"
          className="text-[#1AA0DD] hover:text-[#0E7AAF] font-semibold"
        >
          guarantee@meinhaus.ca
        </a>{" "}
        or open a claim directly from your project dashboard.
      </p>
    ),
  },
];

const GuaranteeTerms = () => (
  <LegalPage
    eyebrow="The Done-Right Guarantee"
    title="Guarantee Terms"
    lede="Plain-English coverage for the work you booked through MeinHaus. If it isn't right, we make it right."
    updated="February 8, 2026"
    sections={sections}
  />
);

export default GuaranteeTerms;
